import type { Comic } from '@tanko/shared'
import { isBackedUp } from './comic-sync'
import { newQueueEntry, type BackupQueueEntry } from './backup-queue-entry'

/** 排程只看得到作品的這幾個欄位，測試因此不需要造出完整的 Comic。 */
export type QueueCandidate = Pick<Comic, 'id' | 'indexed' | 'syncState'>

export interface QueueReconciliation {
  /** 需要寫回的項目（新增或狀態需要修正），內容是完整的一筆。 */
  upserts: BackupQueueEntry[]
  /** 已不該留在佇列裡的項目：作品已從書庫移除，或已經備份完成。 */
  removedIds: string[]
}

/**
 * 依書庫目前的作品把佇列補齊。
 *
 * 排程是自動的，執行不是。這裡只決定「佇列該有哪些項目、各自在什麼狀態」，不啟動任何
 * 上傳——`requestPermission()` 必須在使用者手勢的呼叫堆疊內，沒有手勢時未授權的來源
 * 讀不到任何位元組，因此至少需要一次明確的開始。
 *
 * 已備份的作品**不留佇列項**：「這本備份好了」的真相是 `comic.syncState`，佇列項只是
 * 重複記一次，留著只會讓明細清單隨藏書一起無限長下去。已完成的項目因此在下一次排程時
 * 收掉；那一輪跑了幾本由執行結果自己記著，不靠這張表。
 *
 * 兩種需要修正既有項目的情形：
 * - `running` 是上一次被中斷留下的，復原為 `queued`，否則那一本會永遠卡在執行中。
 * - `done` 但作品已不是已備份（例如雲端副本被刪掉），重新排入。
 */
export function reconcileQueue(
  comics: readonly QueueCandidate[], existing: readonly BackupQueueEntry[], now: number,
): QueueReconciliation {
  const entries = new Map(existing.map(entry => [entry.comicId, entry]))
  const upserts: BackupQueueEntry[] = []
  const obsolete: string[] = []
  const seen = new Set<string>()

  for (const comic of comics) {
    // 尚未完成索引的作品頁數還不確定，不能上傳，也就不該排進佇列。
    if (!comic.indexed) continue
    seen.add(comic.id)
    const entry = entries.get(comic.id)
    if (isBackedUp(comic)) {
      // 已備份就不該再有佇列項，不論它現在是什麼狀態。
      if (entry) obsolete.push(comic.id)
      continue
    }
    if (!entry) { upserts.push(newQueueEntry(comic.id, now)); continue }
    if (entry.status === 'running' || entry.status === 'done') {
      upserts.push({ ...entry, status: 'queued', reason: '', updatedAt: now })
    }
  }

  // 已不在書庫的，加上已備份因此不需要再留紀錄的。
  const gone = existing.filter(entry => !seen.has(entry.comicId)).map(entry => entry.comicId)
  return { upserts, removedIds: [...gone, ...obsolete] }
}

/**
 * 執行順序：`queuedAt` 由小到大，同時排入的以書庫順序為準。
 *
 * 順序用 `queuedAt` 而不是另存一個序號，是因為「插到最前面」只要給一個比目前所有待處理
 * 項都小的值就成立，而且這個順序跟著項目一起持久化，重新整理後不會重排。
 */
export function orderQueue(
  entries: readonly BackupQueueEntry[], libraryOrder: readonly string[],
): BackupQueueEntry[] {
  const rank = new Map(libraryOrder.map((id, index) => [id, index]))
  return [...entries].sort((left, right) =>
    left.queuedAt - right.queuedAt || (rank.get(left.comicId) ?? 0) - (rank.get(right.comicId) ?? 0))
}

/** 跑完一本的結果。systemic 的失敗會暫停整個佇列，item 的只影響那一本。 */
export type QueueOutcome =
  | { kind: 'done' }
  | { kind: 'skipped'; reason: string }
  | { kind: 'failed'; reason: string; failure: 'item' | 'systemic' }

export interface BackupQueueDeps {
  /** 下一本要處理的作品識別碼；每一本開始前重讀，已從書庫移除的因此自然消失。 */
  next: () => string | undefined
  /** 實際跑一本。由呼叫端接到既有的單本上傳流程。 */
  run: (comicId: string) => Promise<QueueOutcome>
  /** 狀態變更立即寫回持久層，中斷後才看得到做到哪裡。 */
  record: (comicId: string, status: BackupQueueEntry['status'], reason: string) => Promise<void>
  /** 使用者要求停在這一本結束之後。 */
  stopRequested: () => boolean
}

export interface QueueRunSummary {
  processed: number
  done: number
  failed: number
  skipped: number
  paused: boolean
  /** paused 為 true 時的系統性原因；否則為空字串。 */
  pausedReason: string
}

/**
 * 逐本執行，一次一本。
 *
 * 單本失敗記錄原因後繼續下一本；系統性失敗立即停下並把那一本放回 queued——那不是這本
 * 書的問題，排除原因之後應該從它繼續，而不是把它記成失敗跳過去。
 */
export async function runBackupQueue(deps: BackupQueueDeps): Promise<QueueRunSummary> {
  const summary: QueueRunSummary = { processed: 0, done: 0, failed: 0, skipped: 0, paused: false, pausedReason: '' }
  const attempted = new Set<string>()
  for (;;) {
    if (deps.stopRequested()) break
    const comicId = deps.next()
    // 同一輪內重複出現代表狀態沒有前進（例如寫回失敗），停下來比空轉好。
    if (!comicId || attempted.has(comicId)) break
    attempted.add(comicId)
    await deps.record(comicId, 'running', '')
    const outcome = await deps.run(comicId)
    summary.processed += 1
    if (outcome.kind === 'done') {
      await deps.record(comicId, 'done', '')
      summary.done += 1
      continue
    }
    if (outcome.kind === 'skipped') {
      await deps.record(comicId, 'skipped', outcome.reason)
      summary.skipped += 1
      continue
    }
    if (outcome.failure === 'systemic') {
      await deps.record(comicId, 'queued', outcome.reason)
      summary.paused = true
      summary.pausedReason = outcome.reason
      break
    }
    await deps.record(comicId, 'failed', outcome.reason)
    summary.failed += 1
  }
  return summary
}

/**
 * 備份列要不要呈現。
 *
 * 綁在「能不能備份」而不是「有沒有事情做」：只在有待備份時才出現的話，使用者既找不到
 * 入口也看不出目前狀態。反過來說，備份要讀本地檔案，而 File System Access 只有桌面版
 * Chromium 支援——iPad 上不支援、也不可能有本地作品，因此那裡自然不呈現，不需要為它
 * 另開一個永遠是空的分頁。
 */
export function showsBackupBar(supportsLocalFiles: boolean, hasSources: boolean): boolean {
  return supportsLocalFiles && hasSources
}
